import axios, { AxiosError } from 'axios';
import useAuthStore from '../store/auth';

// Global refresh promise to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<string> | null = null;

// Auto-detect API base URL based on environment
const getApiBaseUrl = (): string => {
  // In development, use local Express server with /api prefix
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  }
  // In production, use Vercel serverless API routes
  return '/api';
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status === 401) {
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                            originalRequest?.url?.includes('/auth/refresh') ||
                            originalRequest?.url?.includes('/auth/logout');

      if (isAuthEndpoint) {
        useAuthStore.getState().logout();
        delete api.defaults.headers.common['Authorization'];
        return Promise.reject(error);
      }

      if (!originalRequest?._retry) {
        originalRequest._retry = true;

        // If refresh is already in progress, wait for it
        if (refreshPromise) {
          try {
            const newToken = await refreshPromise;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // Start refresh process
        refreshPromise = (async () => {
          try {
            // Check if we have a refresh token cookie
            const response = await api.post('/auth?action=refresh');
            const { accessToken } = response.data;

            // Update stored token
            localStorage.setItem('accessToken', accessToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

            // Update activity timestamp
            useAuthStore.getState().updateActivity();

            return accessToken;
          } catch (refreshError: unknown) {
            console.error('Token refresh failed:', refreshError);

            // Check if it's a network error (not auth-related)
            const axiosError = refreshError as AxiosError;
            const isNetworkError = !axiosError.response ||
                                 axiosError.code === 'NETWORK_ERROR' ||
                                 axiosError.message?.includes('Network Error');

            if (isNetworkError) {
              // For network errors, don't logout immediately - let the user retry
              console.warn('Network error during token refresh, keeping session active');
              throw new Error('NETWORK_ERROR');
            }

            // For auth errors (401, invalid token, etc.), logout
            localStorage.removeItem('accessToken');
            localStorage.removeItem('lastActivity');
            useAuthStore.getState().logout();
            delete api.defaults.headers.common['Authorization'];

            // Redirect to login if not already there
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }

            throw refreshError;
          } finally {
            refreshPromise = null; // Reset the promise
          }
        })();

        try {
          const newToken = await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;